import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE, getPhone, BackBar, Chip, Card, PrimaryBtn, AIOutput, WhatsAppBtn, useCoachStream } from './healthShared';

const MEAL_TYPES = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Night duty', 'Travel', 'Conference'];

const MEALS = {
  Breakfast: [
    { n: 'Moong dal chilla + curd', t: ['Veg', 'High-protein'] },
    { n: '2-3 egg omelette + 1 multigrain toast', t: ['Non-veg', 'High-protein'] },
    { n: 'Vegetable poha + handful of peanuts', t: ['Veg'] },
    { n: 'Idli (2-3) + sambar', t: ['Veg', 'Light'] },
    { n: 'Greek yogurt / dahi + fruit + nuts', t: ['Veg', 'Quick'] },
  ],
  Lunch: [
    { n: 'Dal + roti (2) + sabzi + salad', t: ['Veg', 'Balanced'] },
    { n: 'Grilled chicken / fish + rice + salad', t: ['Non-veg', 'High-protein'] },
    { n: 'Rajma or chole + brown rice + curd', t: ['Veg', 'High-protein'] },
    { n: 'Paneer bhurji + roti + salad', t: ['Veg', 'High-protein'] },
    { n: 'Curd rice + vegetable stir-fry', t: ['Veg', 'Light'] },
  ],
  Dinner: [
    { n: 'Vegetable khichdi + curd', t: ['Veg', 'Light'] },
    { n: 'Grilled fish / chicken + sautéed vegetables', t: ['Non-veg', 'High-protein'] },
    { n: 'Palak paneer + 1-2 roti', t: ['Veg', 'High-protein'] },
    { n: 'Dal + jeera rice (small) + salad', t: ['Veg'] },
    { n: 'Vegetable soup + besan chilla', t: ['Veg', 'Weight-management'] },
  ],
  Snack: [
    { n: 'Roasted chana / makhana', t: ['Veg', 'High-protein'] },
    { n: 'Fruit + 10-12 almonds', t: ['Veg', 'Quick'] },
    { n: 'Boiled eggs (2)', t: ['Non-veg', 'High-protein'] },
    { n: 'Buttermilk / chaas + handful peanuts', t: ['Veg', 'Light'] },
    { n: 'Sprouts chaat', t: ['Veg', 'High-protein'] },
  ],
  'Night duty': [
    { n: 'Curd + poha / upma (light, pre-duty)', t: ['Veg', 'Light'] },
    { n: 'Boiled eggs + fruit (mid-shift)', t: ['Non-veg', 'High-protein'] },
    { n: 'Roasted chana + chaas instead of chips', t: ['Veg'] },
    { n: 'Vegetable sandwich (skip fried canteen food)', t: ['Veg', 'Practical'] },
    { n: 'Light khichdi after shift, then sleep', t: ['Veg', 'Recovery'] },
  ],
  Travel: [
    { n: 'Nuts + fruit (carry-on snack)', t: ['Veg', 'Quick'] },
    { n: 'Idli / dosa over fried food at stops', t: ['Veg', 'Light'] },
    { n: 'Grilled sandwich / wrap (skip chips + cola)', t: ['Practical'] },
    { n: 'Curd rice — easy on the stomach', t: ['Veg', 'Light'] },
    { n: 'Protein bar / roasted chana in your bag', t: ['High-protein'] },
  ],
  Conference: [
    { n: 'Breakfast: eggs / idli, skip pastries', t: ['High-protein'] },
    { n: 'Buffet rule: half plate salad + one protein', t: ['Balanced'] },
    { n: 'Tandoori / grilled starters over fried', t: ['Non-veg', 'High-protein'] },
    { n: 'One dessert, smallest portion, enjoy it', t: ['Practical'] },
    { n: 'Water between coffees — aim 1 glass/hour', t: ['Hydration'] },
  ],
};

const QUICK_ASKS = ['I am at the hospital cafeteria', 'I am travelling', 'I have only 10 minutes', 'I am hungry after night duty'];

export default function EatBetter({ onBack, profile, onPrefsSaved }) {
  const [meal, setMeal] = useState('Breakfast');
  const [ask, setAsk] = useState('');
  const [likes, setLikes] = useState(profile?.food_likes || '');
  const [dislikes, setDislikes] = useState(profile?.food_dislikes || '');
  const { output, running, run, stop } = useCoachStream();
  const plan = useCoachStream();

  const askNow = (text) => {
    const q = text || ask;
    if (!q.trim()) return;
    run(`What can I eat now? My situation: ${q}`, 'eat_now');
  };

  const makePlan = async () => {
    if (!likes.trim() && !dislikes.trim()) {
      toast.error('Tell us at least one food you like or dislike');
      return;
    }
    try {
      await axios.post(`${API_BASE}/api/health-hub/preferences`, { phone: getPhone(), food_likes: likes, food_dislikes: dislikes });
      onPrefsSaved?.(likes, dislikes);
    } catch { /* no profile yet — plan still works */ }
    plan.run(`Create my personalised 7-day Indian diet plan. Foods I like: ${likes || 'no specific likes'}. Foods I dislike / must avoid: ${dislikes || 'none'}.`, 'diet_plan');
  };

  return (
    <div data-testid="eat-better-section">
      <BackBar title="Eat Better" onBack={onBack} />

      <p className="text-xs font-black tracking-widest uppercase text-[#7FA183] mb-3">What should I eat?</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {MEAL_TYPES.map((m) => (
          <Chip key={m} active={meal === m} onClick={() => setMeal(m)} testId={`meal-type-${m.toLowerCase().replace(/\s/g, '-')}`}>{m}</Chip>
        ))}
      </div>
      <Card className="p-5 mb-6" testId="meal-options">
        <div className="space-y-3">
          {MEALS[meal].map((o) => (
            <div key={o.n} className="flex items-start justify-between gap-3 pb-3 border-b border-[#F0F2E8] last:border-0 last:pb-0">
              <p className="text-sm font-semibold text-[#33402F]">{o.n}</p>
              <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                {o.t.map((t) => (
                  <span key={t} className="text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#EDF2EB] text-[#5D7A62]">{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[#9AA594] mt-4">No crash diets. Pick what's realistic today.</p>
      </Card>

      <p className="text-xs font-black tracking-widest uppercase text-[#D97B5A] mb-3">What can I eat now?</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {QUICK_ASKS.map((q) => (
          <button key={q} onClick={() => { setAsk(q); askNow(q); }} data-testid={`eat-quick-${q.split(' ').pop().toLowerCase()}`} className="px-3 py-2 rounded-full text-xs font-semibold bg-[#FBF3EE] text-[#A85A3E] border border-[#F0DDD2] hover:bg-[#F5E4D9] transition-colors">
            “{q}”
          </button>
        ))}
      </div>
      <Card className="p-4">
        <div className="flex gap-2">
          <input
            value={ask}
            onChange={(e) => setAsk(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && askNow()}
            placeholder="Type your situation…"
            data-testid="eat-now-input"
            className="flex-1 px-4 py-3 rounded-2xl bg-[#F8F8F2] border border-[#E3E5D8] text-sm text-[#273127] focus:outline-none focus:border-[#7FA183]"
          />
          {running ? (
            <PrimaryBtn onClick={stop} testId="eat-now-stop-btn" className="bg-[#A85A3E]">Stop</PrimaryBtn>
          ) : (
            <PrimaryBtn onClick={() => askNow()} testId="eat-now-ask-btn">Ask</PrimaryBtn>
          )}
        </div>
        {(output || running) && (
          <div className="mt-4 pt-4 border-t border-[#F0F2E8]">
            {running && !output && <p className="text-sm text-[#9AA594] animate-pulse">Thinking of practical options…</p>}
            <AIOutput text={output} testId="eat-now-output" />
            {!running && output && <div className="mt-3"><WhatsAppBtn text={output} testId="eat-whatsapp-btn" /></div>}
          </div>
        )}
      </Card>

      {/* My Diet Plan */}
      <p className="text-xs font-black tracking-widest uppercase text-[#7FA183] mb-3 mt-6">My Diet Plan</p>
      <Card className="p-5" testId="diet-plan-card">
        <p className="text-sm text-[#66735F] mb-4">Your own 7-day Indian plan, built around what you actually enjoy eating.</p>
        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-xs font-bold tracking-wide uppercase text-[#66735F] mb-1.5">Foods I like 😋</label>
            <input
              value={likes}
              onChange={(e) => setLikes(e.target.value)}
              placeholder="e.g. paneer, fish curry, idli, mango…"
              data-testid="diet-likes-input"
              className="w-full px-4 py-3 rounded-2xl bg-[#F8F8F2] border border-[#E3E5D8] text-sm text-[#273127] focus:outline-none focus:border-[#7FA183]"
            />
          </div>
          <div>
            <label className="block text-xs font-bold tracking-wide uppercase text-[#66735F] mb-1.5">Foods I dislike / avoid 🚫</label>
            <input
              value={dislikes}
              onChange={(e) => setDislikes(e.target.value)}
              placeholder="e.g. karela, mushrooms, too-spicy food…"
              data-testid="diet-dislikes-input"
              className="w-full px-4 py-3 rounded-2xl bg-[#F8F8F2] border border-[#E3E5D8] text-sm text-[#273127] focus:outline-none focus:border-[#7FA183]"
            />
          </div>
        </div>
        {plan.running ? (
          <PrimaryBtn onClick={plan.stop} testId="diet-plan-stop-btn" className="w-full py-4 bg-[#A85A3E]">Stop</PrimaryBtn>
        ) : (
          <PrimaryBtn onClick={makePlan} testId="diet-plan-btn" className="w-full py-4 text-base">CREATE MY 7-DAY DIET PLAN</PrimaryBtn>
        )}
        {(plan.output || plan.running) && (
          <div className="mt-4 pt-4 border-t border-[#F0F2E8]">
            {plan.running && !plan.output && <p className="text-sm text-[#9AA594] animate-pulse">Building your 7-day plan around your tastes…</p>}
            <AIOutput text={plan.output} testId="diet-plan-output" />
            {!plan.running && plan.output && <div className="mt-3"><WhatsAppBtn text={plan.output} testId="diet-plan-whatsapp-btn" /></div>}
          </div>
        )}
      </Card>
    </div>
  );
}
